import { useState } from 'react';
import { FileEdit, ChevronDown, ChevronRight } from 'lucide-react';
import {
  AmendmentDiffCopy,
  amendmentDiffCategoryLabel,
  amendmentDiffChangeLabel,
  assertNever,
} from '@signalvote/shared';
import type {
  AmendmentDiffDto,
  AmendmentDiffChange,
  AmendmentDiffRowDto,
  AmendmentDiffSectionDto,
} from '@signalvote/shared';
import { cn } from '@/lib/utils';
import { formatAmount, formatDate } from '../utils';

interface AmendmentDiffPanelProps {
  /** The correction this report represents. Null on an original report — renders nothing. */
  diff: AmendmentDiffDto | null;
}

/**
 * Issue #690 — what an amendment changed, at the top of its report-detail page.
 *
 * An amendment is a complete replacement filing: the FEC never asks what moved, so the
 * format carries no record of it and this screen is the only place the question can be
 * answered. Without it a treasurer standing at the upload button either uploads on faith or
 * reconstructs the difference by hand from two screens.
 *
 * Read-only. It renders the server's join of the predecessor's frozen as-filed package
 * against this amendment's assembled rows; it changes nothing about what gets filed.
 */
export function AmendmentDiffPanel({ diff }: AmendmentDiffPanelProps) {
  const [expanded, setExpanded] = useState(true);
  if (!diff) return null;

  // A schedule with nothing on either filing is noise, not reassurance — drop it. One that
  // carries untouched rows stays, because "and nothing else moved" is half the question.
  const sections = diff.sections.filter(
    (s) => s.rows.length > 0 || s.netChange !== 0 || s.unchangedCount > 0,
  );
  const movedRowCount = sections.reduce((n, s) => n + s.rows.length, 0);

  return (
    <div
      className="rounded-lg border border-orange-200 bg-orange-50 p-4 text-orange-950"
      data-testid="amendment-diff-panel"
    >
      <div className="flex items-start gap-2">
        <FileEdit className="mt-0.5 h-4 w-4 shrink-0 text-orange-700" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-semibold">{AmendmentDiffCopy.Heading}</p>
              <p className="mt-0.5 text-sm">
                {AmendmentDiffCopy.correcting(diff.baselineReportName, diff.baselineAmendmentNumber)}
                {diff.baselineFiledDate && (
                  <span className="text-orange-800">
                    {' '}
                    {AmendmentDiffCopy.filedOn(formatDate(diff.baselineFiledDate))}
                  </span>
                )}
              </p>
            </div>
            {diff.hasAsFiledSnapshot && (
              <button
                type="button"
                onClick={() => setExpanded((e) => !e)}
                className="flex shrink-0 items-center gap-1 text-xs font-medium text-orange-800 hover:text-orange-950"
                data-testid="amendment-diff-toggle"
                aria-expanded={expanded}
              >
                {expanded ? (
                  <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
                ) : (
                  <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
                )}
                {expanded ? AmendmentDiffCopy.HideDetail : AmendmentDiffCopy.ShowDetail}
              </button>
            )}
          </div>

          {!diff.hasAsFiledSnapshot ? (
            <p className="mt-2 text-sm" data-testid="amendment-diff-no-snapshot">
              {AmendmentDiffCopy.NoSnapshot}
            </p>
          ) : movedRowCount === 0 ? (
            <p className="mt-2 text-sm" data-testid="amendment-diff-nothing-changed">
              {AmendmentDiffCopy.NothingChanged}
            </p>
          ) : (
            expanded && (
              <>
                <p className="mt-2 text-xs text-orange-800">{AmendmentDiffCopy.Subhead}</p>
                <div className="mt-3 space-y-4">
                  {sections.map((section) => (
                    <DiffSection key={section.category} section={section} />
                  ))}
                </div>
              </>
            )
          )}
        </div>
      </div>
    </div>
  );
}

function DiffSection({ section }: { section: AmendmentDiffSectionDto }) {
  return (
    // Capped to a document measure: the two money columns only read as a before/after pair
    // while they sit near the name they belong to. Left to stretch on a wide screen they drift
    // to opposite edges and the comparison stops being one.
    <div className="max-w-2xl" data-testid={`amendment-diff-section-${section.category}`}>
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 border-b border-orange-200 pb-1">
        <span className="text-sm font-medium">{amendmentDiffCategoryLabel(section.category)}</span>
        <span className="text-xs text-orange-800">
          {formatAmount(section.asFiledTotal)} → {formatAmount(section.amendedTotal)}
        </span>
        {section.netChange !== 0 && (
          <span className="text-xs font-semibold">{formatSigned(section.netChange)}</span>
        )}
        {section.unchangedCount > 0 && (
          <span className="ml-auto text-xs text-orange-800">
            {AmendmentDiffCopy.unchanged(section.unchangedCount)}
          </span>
        )}
      </div>
      {section.rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="mt-1 w-full text-sm">
            {/* Both money columns are visibly labelled. Which figure is the old one and which is
                the new one is the entire question the panel exists to answer — inferring it from
                left-to-right order is exactly the guess a treasurer should not have to make. */}
            <thead>
              <tr className="text-xs font-normal text-orange-800">
                <th scope="col" className="sr-only">
                  Change
                </th>
                <th scope="col" className="sr-only">
                  Who
                </th>
                <th scope="col" className="py-0.5 pr-2 text-right font-normal whitespace-nowrap">
                  {AmendmentDiffCopy.AsFiledHeader}
                </th>
                <th scope="col" className="py-0.5 text-right font-normal whitespace-nowrap">
                  {AmendmentDiffCopy.AmendedHeader}
                </th>
              </tr>
            </thead>
            <tbody>
              {section.rows.map((row) => (
                <DiffRow key={row.entityId} row={row} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function DiffRow({ row }: { row: AmendmentDiffRowDto }) {
  return (
    <tr className="align-top" data-testid={`amendment-diff-row-${row.entityId}`}>
      <td className="py-1 pr-2 whitespace-nowrap">
        <span
          className={cn(
            'rounded-full px-2 py-0.5 text-xs font-medium',
            changeBadgeClassName(row.change),
          )}
        >
          {amendmentDiffChangeLabel(row.change)}
        </span>
      </td>
      <td className="w-full py-1 pr-2">
        {row.name ?? (
          <span className="text-orange-800 italic">{AmendmentDiffCopy.UnknownCounterparty}</span>
        )}
      </td>
      <td className="py-1 pr-2 text-right whitespace-nowrap tabular-nums">
        <Figure amount={row.asFiledAmount} date={row.asFiledDate} />
      </td>
      <td className="py-1 text-right whitespace-nowrap tabular-nums">
        <Figure amount={row.amendedAmount} date={row.amendedDate} />
      </td>
    </tr>
  );
}

/** One side of a row's comparison; an em-dash where that filing carried nothing. */
function Figure({ amount, date }: { amount: number | null; date: string | null }) {
  if (amount == null) return <span className="text-orange-700">—</span>;
  return (
    <>
      <span className="font-medium">{formatAmount(amount)}</span>
      {date && <span className="ml-1 text-xs text-orange-800">{formatDate(date)}</span>}
    </>
  );
}

function changeBadgeClassName(change: AmendmentDiffChange): string {
  switch (change) {
    case 'Added':
      return 'bg-green-100 text-green-800';
    case 'Altered':
      return 'bg-amber-100 text-amber-800';
    case 'Removed':
      return 'bg-red-100 text-red-800';
    default:
      return assertNever(change);
  }
}

/** "+$50.00" / "-$250.00" — the sign is the point, so it is never dropped. */
function formatSigned(amount: number): string {
  return amount > 0 ? `+${formatAmount(amount)}` : formatAmount(amount);
}
